import { produce } from 'immer';
import { type PairingV1 as Pairing } from '@hungryroot/rest';

type FavoritesState = Record<string, boolean | undefined>;

interface GetFilteredRecipesAction {
  type: 'GET_FILTERED_RECIPES';
  dishTypeCount: {
    [k: number]: number;
  };
  loadMore: boolean;
  recipes: Pairing[];
  totalCount?: number;
}

interface RecipesFavoriteChangeAction {
  type: 'RECIPE_FAVORITE_CHANGE';
  isFavorite: boolean;
  recipeId: number;
}

interface GetPairingsCollectionsAction {
  type: 'GET_PAIRINGS_COLLECTIONS';
  collections: Record<
    string,
    {
      pairings: Pairing[];
    }
  >;
}

interface AddRecipeAction {
  type: 'ADD_RECIPE';
  recipe: Pairing;
}

type FavoritesActions =
  | GetFilteredRecipesAction
  | RecipesFavoriteChangeAction
  | GetPairingsCollectionsAction
  | AddRecipeAction;

export function recipeFavoritedChange(
  recipeId: number,
  isFavorite: boolean
): RecipesFavoriteChangeAction {
  return { type: 'RECIPE_FAVORITE_CHANGE', recipeId, isFavorite };
}

/*
  This reducer is used with the legacy Customize screens.

  It is not a "useReducer" reducer.
*/
export function reducer(state: FavoritesState = {}, action: FavoritesActions) {
  switch (action.type) {
    case 'ADD_RECIPE': {
      const { recipe } = action;
      const { id: pairingId, is_favorited } = recipe;
      return produce(state, (draft) => {
        if (typeof is_favorited === 'boolean') {
          draft[pairingId] = is_favorited;
        }
      });
    }
    case 'GET_PAIRINGS_COLLECTIONS': {
      const { collections } = action;
      const keys = Object.keys(collections) as Array<keyof typeof collections>;
      return produce(state, (draft) => {
        keys.forEach((key) => {
          const { pairings } = collections[key];
          pairings.forEach((pairing) => {
            const { is_favorited, id: recipeId } = pairing;
            if (typeof is_favorited === 'boolean') {
              draft[recipeId] = is_favorited;
            }
          });
        });
      });
    }
    case 'GET_FILTERED_RECIPES': {
      const { recipes } = action;
      return produce(state, (draft) => {
        recipes.forEach((recipe) => {
          const { id: recipeId, is_favorited } = recipe;
          if (typeof is_favorited === 'boolean') {
            draft[recipeId] = is_favorited;
          }
        });
      });
    }
    case 'RECIPE_FAVORITE_CHANGE': {
      const { isFavorite, recipeId } = action;
      return produce(state, (draft) => {
        draft[recipeId] = isFavorite;
      });
    }
  }

  return state;
}
